import { RiFileCopyLine } from '@remixicon/react';
import {
  Button,
  Flex,
  InfraCodeBlock,
  InfraSection,
  Text,
} from '../ui/InfraPrimitives';

export interface LabKubeParts {
  server: string;
  caData: string;
  token: string;
}

export function labContextNameFromCluster(
  clusterName?: string,
  environmentName?: string,
): string {
  const slug = (clusterName || environmentName || 'k8s')
    .replace(/[^a-zA-Z0-9-_]/g, '-')
    .toLowerCase();
  return `ps-lab-${slug}`;
}

function shellSingleQuote(value: string): string {
  return `'${value.replace(/'/g, `'\"'\"'`)}'`;
}

export function parseLabKubeconfigFromBase64(b64: string): LabKubeParts | null {
  try {
    const yaml = atob(b64.replace(/\s+/g, ''));
    const serverMatch = yaml.match(/^\s*server:\s*(\S+)\s*$/m);
    const caMatch = yaml.match(/certificate-authority-data:\s*(\S+)/);
    const tokenQuoted = yaml.match(/^\s*token:\s*"(.*)"\s*$/m);
    const tokenBare = yaml.match(/^\s*token:\s*(\S+)\s*$/m);
    const token = (tokenQuoted?.[1] ?? tokenBare?.[1])?.trim();
    if (!serverMatch?.[1] || !caMatch?.[1] || !token) {
      return null;
    }
    return {
      server: serverMatch[1],
      caData: caMatch[1],
      token,
    };
  } catch {
    return null;
  }
}

export function buildLabSetupScript(parts: LabKubeParts, contextName: string): string {
  const clusterKey = `${contextName}-cluster`;
  const userKey = `${contextName}-user`;
  return [
    'set -eu',
    'mkdir -p ~/.kube',
    'touch ~/.kube/config',
    `LAB_CTX=${shellSingleQuote(contextName)}`,
    `CLUSTER_KEY=${shellSingleQuote(clusterKey)}`,
    `USER_KEY=${shellSingleQuote(userKey)}`,
    `LAB_SERVER=${shellSingleQuote(parts.server)}`,
    'case "$LAB_SERVER" in',
    '  *localhost:8080*|http://127.0.0.1:8080*)',
    '    echo "ERROR: invalid API server $LAB_SERVER"; exit 1 ;;',
    'esac',
    'kubectl config delete-context "$LAB_CTX" 2>/dev/null || true',
    'kubectl config delete-cluster "$CLUSTER_KEY" 2>/dev/null || true',
    'kubectl config delete-user "$USER_KEY" 2>/dev/null || true',
    'CA_TMP="$(mktemp)"',
    `echo ${shellSingleQuote(parts.caData)} | base64 -d > "$CA_TMP"`,
    'kubectl config set-cluster "$CLUSTER_KEY" --server="$LAB_SERVER" ' +
      '--certificate-authority="$CA_TMP" --embed-certs=true',
    'rm -f "$CA_TMP"',
    `kubectl config set-credentials "$USER_KEY" --token=${shellSingleQuote(parts.token)}`,
    'kubectl config set-context "$LAB_CTX" --cluster="$CLUSTER_KEY" --user="$USER_KEY"',
    'kubectl config use-context "$LAB_CTX"',
    'echo "Lab cluster API: $LAB_SERVER"',
    'kubectl get ns',
  ].join('\n');
}

export const LAB_SETUP_SCRIPT_PREVIEW = `set -eu
mkdir -p ~/.kube
LAB_CTX='ps-lab-<cluster>'
kubectl config set-cluster … --server='https://…' --certificate-authority="$CA_TMP" --embed-certs=true
kubectl config set-credentials … --token='…'
kubectl config set-context "$LAB_CTX" …
kubectl config use-context "$LAB_CTX"
kubectl get ns`;

type LabKubectlSectionProps = {
  cloudLabel: string;
  labKubeconfigBase64?: string;
  labAccessServiceAccount?: string;
  labAccessNamespace?: string;
  labContextName: string;
  onCopySetupScript: () => void;
};

export function LabKubectlSection({
  cloudLabel,
  labKubeconfigBase64,
  labAccessServiceAccount,
  labAccessNamespace,
  labContextName,
  onCopySetupScript,
}: LabKubectlSectionProps) {
  if (!labKubeconfigBase64 && !labAccessServiceAccount) {
    return null;
  }

  return (
    <InfraSection title={`🧪 Lab kubectl (no ${cloudLabel} admin required)`}>
      <Text variant="body-medium">
        {cloudLabel} IAM may block <code>Role</code> / operator installs. This adds a cluster-admin
        context (ServiceAccount <strong>{labAccessServiceAccount || 'lab-admin'}</strong>, token in
        namespace <strong>{labAccessNamespace || 'default'}</strong>) into your normal{' '}
        <code>~/.kube/config</code> — same workflow as any other cluster.
      </Text>

      {labKubeconfigBase64 && (
        <>
          <Text variant="title-small" style={{ marginTop: '1rem' }}>
            One-time setup
          </Text>
          <Text variant="body-medium">
            Click <strong>Copy setup script</strong>, paste in Terminal, press Enter. A short script
            (~15 lines) adds context <code>{labContextName}</code> to <code>~/.kube/config</code>; your
            other contexts are unchanged.
          </Text>
          <Button
            variant="primary"
            size="small"
            onPress={onCopySetupScript}
            style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}
          >
            <RiFileCopyLine size={16} />
            Copy setup script
          </Button>
          <InfraCodeBlock variant="light">{LAB_SETUP_SCRIPT_PREVIEW}</InfraCodeBlock>

          <Text variant="title-small" style={{ marginTop: '1rem' }}>
            Day to day
          </Text>
          <Text variant="body-medium">
            <code>kubectl config use-context {labContextName}</code>
            <br />
            <code>kubectl create namespace redis-east</code>
            <br />
            <code>kubectl apply -f manifest.yaml -n redis-east</code>
            <br />
            Other cluster: <code>kubectl config use-context &lt;other-context&gt;</code>
          </Text>
        </>
      )}

      <Text variant="body-small" style={{ marginTop: '1rem', display: 'block' }}>
        Treat the lab kubeconfig like a password (full cluster admin). Lab / TTL environment only.
      </Text>
    </InfraSection>
  );
}
